import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { JSONContent } from "@tiptap/react";
import { z } from "zod";

import { RichTextEditorInputWithMention, Row } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";

import { useUpdateJobComment } from "@/hooks/mutations/useUpdateJobComment";
import { useSetJobComment } from "@/hooks/sets/useSetJobComment";
import { tiptapDefaultJson } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";

const updateJobCommentSchema = z.object({
  content: z
    .object({
      type: z.literal("doc"),
      content: z.array(z.any()),
    })
    .refine(
      (data) => {
        return (
          JSON.stringify(data.content) !==
          JSON.stringify(tiptapDefaultJson.content)
        );
      },
      {
        message: "Required",
      },
    ),
});

export const UpdateJobCommentForm = ({
  content,
  jobCommentId,
  onCancel,
  onSuccess,
}: {
  content: JSONContent;
  jobCommentId: number;
  onCancel: () => void;
  onSuccess: () => void;
}) => {
  const { mutate: updateJobComment } = useUpdateJobComment();
  const setJobComment = useSetJobComment();

  const form = useForm<z.infer<typeof updateJobCommentSchema>>({
    defaultValues: {
      // TODO: See if we can get away with not having to cast to this.
      content: content as { type: "doc"; content: unknown[] },
    },
    resolver: zodResolver(updateJobCommentSchema),
  });

  function onSubmit(data: z.infer<typeof updateJobCommentSchema>) {
    mixpanelUtils.trackFormSubmission("Update job comment", MIXPANEL_SCREENS.JOB);
    updateJobComment(
      {
        jobCommentId,
        updateJobCommentRequest: { content: data.content },
      },
      {
        onSuccess: (updatedJobComment) => {
          setJobComment({
            jobCommentToUpdate: updatedJobComment,
          });
          onSuccess();
        },
      },
    );
  }

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-2"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <FormField
          control={form.control}
          name="content"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <RichTextEditorInputWithMention
                  onChange={field.onChange}
                  value={field.value}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Row className="items-center justify-end gap-2">
          <Button
            className="rounded-full"
            onClick={onCancel}
            type="button"
            variant="ghost"
          >
            Cancel
          </Button>
          <Button className="rounded-full" type="submit" variant="outline">
            Update
          </Button>
        </Row>
      </form>
    </Form>
  );
};
